import {computed, DestroyRef, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import {catchError, debounceTime, EMPTY, map, Subject, switchMap, tap} from 'rxjs';
import {BoundTexts} from '../../../core/domain/surveys/model/elements/bound-texts.model';
import {Condition} from '../../../core/domain/surveys/model/conditions/condition.model';
import {CONDITION_TYPE} from '../../../core/domain/surveys/model/conditions/condition.type.model';
import {ConditionPageOption} from '../../../core/domain/surveys/model/conditions/condition-page-option.model';
import {ConditionTarget} from '../../../core/domain/surveys/model/conditions/condition-target.model';
import {ElementPage} from '../../../core/domain/surveys/model/elements/element.page.model';
import {ELEMENT_TYPE} from '../../../core/domain/surveys/model/elements/element.type.model';
import {DisplayMode} from '../../../core/domain/surveys/model/conditions/display-mode.model';
import {
    ALWAYS_DISPLAYED,
    CONDITIONAL_DISPLAY,
    DisplayOptions,
} from '../../../core/domain/surveys/model/conditions/display.options.model';
import {buildFormPage, FormPage} from '../../../core/domain/surveys/model/form.page.model';
import {CES_RANGE} from '../../../core/domain/surveys/model/elements/ces-range.model';
import {CES_TEXT_REPRESENTATION} from '../../../core/domain/surveys/model/elements/ces-text-representation.model';
import {CHOICES_ANSWER_TYPE} from '../../../core/domain/surveys/model/elements/choices-answer-type.model';
import {CHOICES_DISPLAY} from '../../../core/domain/surveys/model/elements/choices-display.model';
import {CHOICES_LAYOUT} from '../../../core/domain/surveys/model/elements/choices-layout.model';
import {CSAT_RANGE} from '../../../core/domain/surveys/model/elements/csat-range.model';
import {DEFAULT_PLATFORM_REVIEW_FORM} from '../../../core/domain/surveys/model/elements/default-platform-review-form.model';
import {DEFAULT_SCALE_RANGE} from '../../../core/domain/surveys/model/elements/default-scale-range.model';
import {DEFAULT_SCALE_START} from '../../../core/domain/surveys/model/elements/default-scale-start.model';
import {GRADUATED_COLORS, RatedColorMode} from '../../../core/domain/surveys/model/elements/rated-color-mode.model';
import {LABEL_KEY} from '../../../core/domain/surveys/model/label-key.model';
import {LabelTranslator} from '../../../core/domain/surveys/model/label-translator.model';
import {newPageQuestionType} from '../../../core/domain/surveys/model/new-page-question-type.model';
import {RATED_REPRESENTATIONS} from '../../../core/domain/surveys/model/elements/rated-representations.model';
import {REPRESENTATION} from '../../../core/domain/surveys/model/elements/representation.model';
import {OPERATOR_LOGICAL} from '../../../core/domain/surveys/model/conditions/operator.logical.model';
import {FormConfiguration} from '../../../core/domain/surveys/model/form.configuration.model';
import {PageConclusion} from '../../../core/domain/surveys/model/page.conclusion.model';
import {PlatformReviewForm} from '../../../core/domain/surveys/model/elements/platform-review-form.model';
import {PageLabels} from '../../../core/domain/surveys/model/page-labels.model';
import {QUESTION_TYPE} from '../../../core/domain/surveys/model/elements/question.type.model';
import {Stage} from '../../../core/domain/surveys/model/stage.model';
import {Survey} from '../../../core/domain/surveys/model/survey.model';
import {SurveyType} from '../../../core/domain/surveys/model/survey.type.model';
import {DEFAULT_STYLE_COLORS} from '../../../core/domain/surveys/model/survey-style-form.model';
import {GoogleWebFont} from '../../../core/domain/font/model/google.webfont.model';
import {SurveyBuilderSelection} from '../../../core/domain/surveys/model/survey-builder-selection.model';
import {SurveyBuilderView} from '../../../core/domain/surveys/model/survey-builder-view.model';
import {SurveyFull} from '../../../core/domain/surveys/model/survey-full.model';
import {SurveyHeader} from '../../../core/domain/surveys/model/survey.header.model';
import {SurveyInformation} from '../../../core/domain/surveys/model/survey.information.model';
import {TargetPlatformReview} from '../../../core/domain/surveys/model/elements/target-platform-review.model';
import {SurveyService} from '../../../core/domain/surveys/survey.service';
import {ThemeConfiguration} from '../../../core/domain/surveys/model/theme.configuration.model';
import {VISUAL_EMOJI} from '../../../core/domain/surveys/model/elements/visual-emoji.model';

/** Laisse le temps de faire glisser un curseur de couleur avant d'envoyer le brouillon au serveur. */
const SAVE_DRAFT_DEBOUNCE_MS = 800;

/** Remplace les libellés d'une page ou d'un élément sans toucher aux traductions des autres langues. */
const withLabels = <T extends {title?: Record<string, string>; description?: Record<string, string>}>(
    labelled: T,
    labels: PageLabels
): T => ({
    ...labelled,
    title: {...labelled.title, [LABEL_KEY]: labels.title},
    description: {...labelled.description, [LABEL_KEY]: labels.description},
});

/** Libellés d'une page ou d'un élément, dans la langue de composition. */
const labelsOf = (labelled?: {title?: Record<string, string>; description?: Record<string, string>}): PageLabels => ({
    title: labelled?.title?.[LABEL_KEY] ?? '',
    description: labelled?.description?.[LABEL_KEY] ?? '',
});

/** La page supprimée emporte la sélection avec elle ; les pages suivantes reculent d'un rang. */
const afterPageDeletion = (
    selection: SurveyBuilderSelection,
    deletedIndex: number,
    remainingCount: number
): SurveyBuilderSelection => {
    if (selection.kind === 'conclusion') return selection;

    if (selection.index === deletedIndex) {
        return {kind: 'page', index: Math.min(deletedIndex, remainingCount - 1)};
    }

    return selection.index > deletedIndex ? {kind: 'page', index: selection.index - 1} : selection;
};

/** La sélection suit la page déplacée, ou se décale si le déplacement l'enjambe. */
const afterPageMove = (selection: SurveyBuilderSelection, from: number, to: number): SurveyBuilderSelection => {
    if (selection.kind === 'conclusion') return selection;

    if (selection.index === from) return {kind: 'page', index: to};

    if (from < to && selection.index > from && selection.index <= to) {
        return {kind: 'page', index: selection.index - 1};
    }

    if (from > to && selection.index >= to && selection.index < from) {
        return {kind: 'page', index: selection.index + 1};
    }

    return selection;
};

/**
 * État partagé de l'éditeur d'enquête, fourni au niveau de la route `surveys/:id/satisfaction/builder`.
 *
 * Le shell `SurveyBuilder` charge la vue complète de l'enquête et la dépose ici ; les panneaux de
 * l'éditeur (en-tête, éléments, zone centrale, pages) la relisent sans la recharger. Volontairement
 * non `providedIn: 'root'` pour rester scopé à une session d'édition et repartir vierge à chaque
 * navigation.
 *
 * Toute l'édition porte sur le brouillon (`draft`) : la version publiée n'est là que pour informer
 * l'utilisateur qu'une version est déjà en ligne. Chaque modification remplace le brouillon en
 * mémoire puis déclenche un enregistrement différé, pour ne pas appeler le serveur à chaque cran
 * d'un sélecteur de couleur.
 */
@Injectable()
export class SurveyBuilderStore {
    private readonly surveyFullSignal = signal<SurveyFull | null>(null);

    private readonly draftSignal = signal<Survey | null>(null);

    private readonly templatesSignal = signal<ElementPage[]>([]);

    private readonly fontsSignal = signal<GoogleWebFont[]>([]);

    /** Les modèles arrivent en vrac ; ils se retrouvent par le type que porte la carte déposée. */
    private readonly templateByType = computed<Map<ELEMENT_TYPE, ElementPage>>(
        () =>
            new Map(
                this.templatesSignal()
                    .filter((template) => !!template.type)
                    .map((template) => [template.type as ELEMENT_TYPE, template])
            )
    );

    private readonly viewSignal = signal<SurveyBuilderView>('elements');

    private readonly selectionSignal = signal<SurveyBuilderSelection>({kind: 'page', index: 0});

    /** Rang de l'élément désigné dans la page, ou `null` quand aucun ne l'est. */
    private readonly selectedElementIndexSignal = signal<number | null>(null);

    /**
     * Compte les demandes de démonstration de l'animation. C'est le changement de valeur qui compte,
     * non la valeur elle-même : rejouer deux fois la même animation doit se voir deux fois.
     */
    private readonly animationRunSignal = signal(0);

    private readonly savingSignal = signal(false);

    private readonly saveFailedSignal = signal(false);

    /**
     * Le brouillon a-t-il été retouché depuis l'ouverture ?
     *
     * L'en-tête arrive avec le `dirtyDraft` du serveur, figé au chargement : sans cette marque, une
     * enquête ouverte publiée le resterait à l'écran quelle que soit la modification apportée, alors
     * que ce qui est édité s'écarte de ce que voient les répondants dès la première frappe.
     */
    private readonly draftEditedSignal = signal(false);

    private readonly draftToSave = new Subject<Survey>();

    private readonly surveyService = inject(SurveyService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly translateService = inject(TranslateService);

    /**
     * De quoi traduire les libellés que le serveur livre en clés, dans la langue de composition.
     *
     * `instant` est une lecture ponctuelle, et c'est ce qu'on veut ici : un libellé posé est du
     * contenu rédigé, il se fige dans la langue où il a été posé et ne suit pas les changements
     * de langue de l'interface — exactement comme s'il avait été saisi.
     */
    private readonly translateLabel: LabelTranslator = (key) => this.translateService.instant(key);

    public readonly surveyFull = this.surveyFullSignal.asReadonly();

    /** Brouillon en cours d'édition : c'est lui, et non celui de `surveyFull`, qui porte les modifications. */
    public readonly draft = this.draftSignal.asReadonly();

    /**
     * Exemplaire vierge de chaque type d'élément, tel que le serveur le définit : bornes d'échelle,
     * choix proposés, options d'affichage. Déposer une carte de la palette, c'est copier le modèle
     * de son type dans la page — le front ne sait pas inventer ce qu'est un NPS ou un CES.
     */
    public readonly templates = this.templatesSignal.asReadonly();

    /** Sans modèles, un dépôt ne pourrait construire qu'une coquille vide : la palette reste inerte. */
    public readonly templatesLoaded = computed<boolean>(() => this.templates().length > 0);

    /** Vue centrale active, choisie dans le rail d'outils. */
    public readonly view = this.viewSignal.asReadonly();

    /** Page en cours d'édition : c'est elle que rend la zone centrale. */
    public readonly selection = this.selectionSignal.asReadonly();

    /**
     * Élément désigné dans la page, celui dont le paramétrage est déplié. Un seul à la fois : deux
     * panneaux ouverts feraient perdre de vue le questionnaire, qui est ce que la zone donne à voir.
     */
    public readonly selectedElementIndex = this.selectedElementIndexSignal.asReadonly();

    public readonly pages = computed<FormPage[]>(() => this.draft()?.pages ?? []);

    public readonly selectedPage = computed<FormPage | PageConclusion | null>(() => {
        const selection = this.selection();
        return selection.kind === 'conclusion'
            ? (this.draft()?.conclusionPage ?? null)
            : (this.pages()[selection.index] ?? null);
    });

    /** Titre et description de la page en cours d'édition, dans la langue de composition. */
    public readonly selectedPageLabels = computed<PageLabels>(() => labelsOf(this.selectedPage() ?? undefined));

    /** Éléments de la page en cours d'édition, dans l'ordre où le répondant les verra. */
    public readonly selectedElements = computed<ElementPage[]>(() => this.selectedPage()?.elements ?? []);

    /** Chaque incrément rejoue l'animation du questionnaire sur la page en cours d'édition. */
    public readonly animationRun = this.animationRunSignal.asReadonly();

    public readonly saving = this.savingSignal.asReadonly();

    public readonly saveFailed = this.saveFailedSignal.asReadonly();

    public readonly surveyHeader = computed<SurveyHeader | null>(() => this.surveyFull()?.header ?? null);

    /** Une version de l'enquête est déjà en ligne, que le brouillon ait été modifié depuis ou non. */
    public readonly hasPublishedVersion = computed<boolean>(() => !!this.surveyFull()?.publishedVersion);

    /**
     * Le brouillon s'écarte de la version publiée : soit le serveur le disait déjà à l'ouverture,
     * soit la session vient de le retoucher.
     *
     * Les deux comptent. `dirtyDraft` ne vaut que pour l'instant du chargement, et l'en-tête n'est
     * pas rechargé à chaque enregistrement — c'est donc l'éditeur qui tient la suite.
     */
    public readonly hasDirtyDraft = computed<boolean>(
        () => !!this.surveyHeader()?.dirtyDraft || this.draftEditedSignal()
    );

    /**
     * Une page ou un élément porte-t-il une condition d'affichage à moitié composée ? L'en-tête le
     * signale : ce qu'elle règle ne s'afficherait jamais, et l'enquête n'est pas diffusable en
     * l'état — autant le dire sans attendre qu'on ouvre la page fautive.
     */
    public readonly hasIncompleteDisplayOptions = computed<boolean>(
        () => this.draft()?.hasIncompleteDisplayOptions() ?? false
    );

    /**
     * Une question qui se mesure étape par étape est-elle restée sans étape ? L'en-tête le signale :
     * ses réponses ne se rattacheraient à rien, et l'enquête n'est pas diffusable en l'état.
     */
    public readonly hasUnstagedQuestions = computed<boolean>(() => this.draft()?.hasUnstagedQuestions() ?? false);

    /**
     * Ce qui manque encore à l'enquête pour être diffusée, sous forme de clés de traduction. Vide,
     * elle est diffusable — c'est ce qui décide du bouton de publication, et ce qu'il annonce quand
     * il est éteint.
     */
    public readonly publicationBlockers = computed<string[]>(() => this.draft()?.publicationBlockers() ?? []);

    public readonly canPublish = computed<boolean>(() => this.publicationBlockers().length === 0);

    /** Habillage du questionnaire : logo, police, couleurs. */
    public readonly themeConfiguration = computed<ThemeConfiguration>(
        () => this.draft()?.information.theme.themeConfiguration ?? {}
    );

    /**
     * Fond du questionnaire, tel que la vue Style le règle.
     *
     * Il porte tout ce que le répondant lit : le bloc de libellés de la page comme chaque carte
     * d'élément. Il vit ici plutôt que dans la zone d'édition parce que les cartes le lisent aussi,
     * et qu'une seconde lecture ailleurs finirait par en diverger.
     *
     * Le repli n'est pas le blanc de `DEFAULT_STYLE_COLORS` : une enquête qui n'impose rien se pose
     * sur le gris très pâle du questionnaire, celui qu'elle aura par défaut chez le répondant.
     */
    public readonly backgroundColor = computed<string>(
        () => this.themeConfiguration().blockSurveyBackground || '#f5f5fb'
    );

    /**
     * Couleur du texte du questionnaire — « Éléments » dans la vue Style. Elle porte les libellés de
     * la page comme ceux de chaque carte : titre, description, choix, critères, bornes.
     *
     * Comme le fond, elle vit ici parce que les cartes la lisent aussi.
     */
    public readonly textColor = computed<string>(
        () => this.themeConfiguration().globalFontColor || DEFAULT_STYLE_COLORS.elements
    );

    /**
     * Couleur des réponses — « Réponses » dans la vue Style. Elle porte ce par quoi le répondant
     * répond, et non ce qu'on lui demande.
     *
     * Elle colore les crans d'une échelle neutre ou d'évaluation, et les choix d'une question à
     * choix unique ou multiple.
     */
    public readonly answerColor = computed<string>(
        () => this.themeConfiguration().answerFontColor || DEFAULT_STYLE_COLORS.answers
    );

    /**
     * Couleur des légendes — « Légendes et critères » dans la vue Style. Elle porte ce qui entoure la
     * réponse sans être la question : les deux extrémités d'une échelle, et le libellé de chaque
     * critère d'une satisfaction multicritères.
     */
    public readonly legendColor = computed<string>(
        () => this.themeConfiguration().legendFontColor || DEFAULT_STYLE_COLORS.legends
    );

    /**
     * Police du questionnaire, choisie dans la vue Style. Vide quand l'enquête n'en impose aucune :
     * la zone d'édition garde alors celle de l'application, la liaison de style s'effaçant d'elle-même.
     *
     * Elle porte, comme le fond et l'encre, tout ce que le répondant lira — titre et description de
     * la page, libellés des cartes, choix, critères, bornes.
     */
    public readonly fontFamily = computed<string>(() => this.themeConfiguration().globalFont?.value ?? '');

    /**
     * Catalogue des polices, chargé une fois pour la session : la vue Style y puise sa liste de
     * choix, et l'éditeur le fichier de celle que l'enquête a retenue.
     */
    public readonly fonts = this.fontsSignal.asReadonly();

    /** Police retenue par l'enquête, telle que le catalogue la décrit — fichiers compris. */
    public readonly selectedFont = computed<GoogleWebFont | undefined>(() =>
        this.fonts().find((font) => font.family === this.fontFamily())
    );

    public readonly setFonts = (fonts: GoogleWebFont[]): void => this.fontsSignal.set(fonts);

    /** Comportement du questionnaire, dont l'animation entre les pages. */
    public readonly formConfiguration = computed<FormConfiguration>(
        () => this.draft()?.information.configuration ?? {}
    );

    constructor() {
        this.draftToSave
            .pipe(
                debounceTime(SAVE_DRAFT_DEBOUNCE_MS),
                tap(() => {
                    this.savingSignal.set(true);
                    this.saveFailedSignal.set(false);
                }),
                // `switchMap` : seul le dernier état du brouillon compte, un enregistrement en cours
                // devient caduc dès qu'une nouvelle modification arrive.
                switchMap((sent) =>
                    this.surveyService.saveDraft(sent).pipe(
                        map((saved) => ({sent, saved})),
                        catchError(() => {
                            this.savingSignal.set(false);
                            this.saveFailedSignal.set(true);
                            // L'erreur ne doit pas rompre le flux : les modifications suivantes
                            // doivent continuer à être enregistrées.
                            return EMPTY;
                        })
                    )
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(({sent, saved}) => {
                this.adoptSavedDraft(sent, saved);
                this.savingSignal.set(false);
            });
    }

    /**
     * Dépose l'enquête à éditer, et ouvre la session sur sa première page.
     *
     * Le rang de la page et celui de l'élément désigné n'ont de sens que pour l'enquête qui les a
     * vus choisir : les garder d'une enquête à l'autre ouvrirait la suivante sur une page qui n'est
     * pas la sienne, voire sur un rang qu'elle n'a pas. La route étant la même pour toutes, le store
     * peut survivre à la navigation — c'est donc ici, au dépôt, que la session repart de zéro.
     */
    public readonly setSurveyFull = (surveyFull: SurveyFull): void => {
        this.surveyFullSignal.set(surveyFull);
        this.selectionSignal.set({kind: 'page', index: 0});
        this.selectedElementIndexSignal.set(null);
        // Les retouches de la session précédente appartenaient à une autre enquête : c'est le
        // `dirtyDraft` de la nouvelle qui reprend la main.
        this.draftEditedSignal.set(false);

        // Une enquête née d'un modèle arrive avec les clés de traduction du serveur. Elles
        // deviennent du texte à l'ouverture, et sont enregistrées aussitôt : sans cet
        // enregistrement, le serveur garderait ses clés de tout un questionnaire auquel
        // l'utilisateur n'aurait rien changé. Le brouillon inchangé revient à l'identique — rien
        // n'est alors renvoyé, et rouvrir une enquête déjà corrigée n'enregistre rien.
        const draft = surveyFull.draft.withTranslatedLabels(this.translateLabel);
        if (draft === surveyFull.draft) {
            this.draftSignal.set(draft);
            return;
        }

        this.replaceDraft(draft);
    };

    public readonly setTemplates = (templates: ElementPage[]): void => this.templatesSignal.set(templates);

    /**
     * Modèle vierge d'un type d'élément. Renvoyé tel quel : c'est à l'appelant de le copier avant de
     * le poser dans une page, sans quoi deux dépôts du même type partageraient le même objet.
     */
    public readonly templateOf = (type: ELEMENT_TYPE): ElementPage | undefined => this.templateByType().get(type);

    public readonly setView = (view: SurveyBuilderView): void => this.viewSignal.set(view);

    /** Rejoue l'animation du questionnaire sur la page en cours d'édition, pour la donner à voir. */
    public readonly playAnimation = (): void => this.animationRunSignal.update((run) => run + 1);

    /** Changer de page referme le paramétrage : il porte sur un élément que l'on quitte. */
    public readonly selectPage = (index: number): void => {
        this.selectionSignal.set({kind: 'page', index});
        this.selectedElementIndexSignal.set(null);
    };

    public readonly selectConclusionPage = (): void => {
        this.selectionSignal.set({kind: 'conclusion'});
        this.selectedElementIndexSignal.set(null);
    };

    /** Désigne l'élément dont on veut régler le paramétrage. */
    public readonly selectElement = (elementIndex: number): void => this.selectedElementIndexSignal.set(elementIndex);

    /**
     * Insère une page juste après celle en cours d'édition et s'y installe : on ajoute une page là
     * où l'on est en train de travailler, pas au bout d'un questionnaire qu'il faudrait ensuite
     * remonter. Les pages suivantes reculent d'un rang. Depuis la page de fin, qui ferme toujours
     * le questionnaire, la page nouvelle se range derrière la dernière page ordinaire.
     *
     * Elle arrive avec une question ouverte, copiée du modèle que le serveur en donne — le même que
     * celui d'un dépôt depuis le catalogue. Laquelle dépend du type de l'enquête
     * (cf. `newPageQuestionType`). Sans modèle chargé, la page arrive vide plutôt que de ne pas
     * arriver du tout : l'utilisateur a demandé une page, il l'obtient.
     */
    public readonly addPage = (): void => {
        const draft = this.draftSignal();
        if (!draft) return;

        const selection = this.selectionSignal();
        const rank =
            selection.kind === 'conclusion' ? draft.pages.length : Math.min(selection.index + 1, draft.pages.length);

        const template = this.templateOf(newPageQuestionType(draft.type));
        const pages = [...draft.pages];
        pages.splice(
            rank,
            0,
            buildFormPage(template ? template.copyForPage().withTranslatedLabels(this.translateLabel) : undefined)
        );

        this.updatePages(pages);
        this.selectPage(rank);
    };

    /**
     * Retire une page du questionnaire, et avec elle les conditions d'affichage qui s'adossaient aux
     * réponses qu'elle recueillait — c'est ce que l'avertissement promet à l'utilisateur avant qu'il
     * confirme. Le questionnaire garde toujours au moins une page : la dernière ne se supprime pas.
     */
    public readonly deletePage = (index: number): void => {
        const draft = this.draftSignal();
        const remaining = this.draftWithoutPage(index);
        if (!draft || !remaining) return;

        this.replaceDraft(this.withoutBrokenConditions(remaining));
        this.selectionSignal.update((selection) => afterPageDeletion(selection, index, draft.pages.length - 1));
    };

    /**
     * La suppression d'une page emporterait-elle des conditions d'affichage ?
     *
     * La page part avec les questions qu'elle posait : ailleurs, tout ce qui s'adossait à l'une de
     * leurs réponses n'aurait plus rien à observer. C'est ce qu'on annonce avant de supprimer.
     */
    public readonly pageDeletionBreaksConditions = (index: number): boolean =>
        this.breaksConditions(this.draftWithoutPage(index));

    /** Le brouillon tel que la suppression d'une page le laisserait ; `null` si elle n'a pas lieu. */
    private readonly draftWithoutPage = (index: number): Survey | null => {
        const draft = this.draftSignal();
        if (!draft || !draft.pages[index] || draft.pages.length <= 1) return null;

        return new Survey({...draft, pages: draft.pages.filter((_, position) => position !== index)});
    };

    /** Déplace une page dans le questionnaire ; la sélection suit la page, pas son ancien rang. */
    public readonly movePage = (from: number, to: number): void => {
        const moved = this.draftWithPageMoved(from, to);
        if (!moved) return;

        this.adoptMovedPage(moved, from, to);
    };

    /**
     * Le déplacement d'une page laisserait-il une condition d'affichage sans la réponse qu'elle
     * observe ? C'est ce qu'on demande avant de déplacer, pour l'annoncer.
     */
    public readonly pageMoveBreaksConditions = (from: number, to: number): boolean =>
        this.breaksConditions(this.draftWithPageMoved(from, to));

    /**
     * Déplace une page en abandonnant les conditions d'affichage que le déplacement rend impossibles
     * — c'est ce que l'avertissement promet à l'utilisateur avant qu'il confirme.
     */
    public readonly movePageDroppingBrokenConditions = (from: number, to: number): void => {
        const moved = this.draftWithPageMoved(from, to);
        if (!moved) return;

        this.adoptMovedPage(this.withoutBrokenConditions(moved), from, to);
    };

    /** Le brouillon tel qu'un déplacement de page le laisserait ; `null` si le déplacement n'a pas lieu. */
    private readonly draftWithPageMoved = (from: number, to: number): Survey | null => {
        const draft = this.draftSignal();
        if (!draft || from === to || !draft.pages[from] || !draft.pages[to]) return null;

        const pages = [...draft.pages];
        const [moved] = pages.splice(from, 1);
        pages.splice(to, 0, moved);

        return new Survey({...draft, pages});
    };

    /** Retient un brouillon dont une page a changé de rang ; la sélection suit la page. */
    private readonly adoptMovedPage = (moved: Survey, from: number, to: number): void => {
        this.replaceDraft(moved);
        this.selectionSignal.update((selection) => afterPageMove(selection, from, to));
    };

    /**
     * Retitre la page en cours d'édition, page de fin comprise. Les deux libellés voyagent ensemble :
     * ils se saisissent dans le même bloc, en tête de la zone d'édition.
     */
    public readonly updateSelectedPageLabels = (labels: PageLabels): void => {
        const draft = this.draftSignal();
        if (!draft) return;

        const selection = this.selectionSignal();

        if (selection.kind === 'conclusion') {
            if (!draft.conclusionPage) return;

            this.replaceDraft(new Survey({...draft, conclusionPage: withLabels(draft.conclusionPage, labels)}));
            return;
        }

        const selectedPage = draft.pages[selection.index];
        if (!selectedPage) return;

        this.updatePages(
            draft.pages.map((page, index) =>
                index === selection.index ? new FormPage(withLabels(page, labels)) : page
            )
        );
    };

    /** Titre et description d'un élément de la page en cours d'édition. */
    public readonly elementLabels = (elementIndex: number): PageLabels =>
        labelsOf(this.selectedElements()[elementIndex]);

    /**
     * Bornes de l'échelle d'un élément de la page en cours d'édition.
     *
     * Elles se lisent ici, et non sur l'élément reçu en entrée par la carte : celui-ci ne vaut que
     * pour la détection de changement en cours, alors que le store est à jour dès l'écriture.
     */
    public readonly elementBoundTexts = (elementIndex: number): BoundTexts | undefined =>
        this.selectedElements()[elementIndex]?.configuration?.boundTexts?.[LABEL_KEY];

    /**
     * Un élément se montre toujours, ou seulement sous conditions. Le mode se lit dans les groupes :
     * aucun groupe, c'est « toujours affiché » — le brouillon ne porte pas le mode lui-même.
     */
    public readonly displayMode = (target: ConditionTarget): DisplayMode =>
        this.displayOptions(target).groups.length > 0 ? CONDITIONAL_DISPLAY : ALWAYS_DISPLAYED;

    /**
     * Passe une cible sous conditions, ou l'en sort.
     *
     * Y passer ouvre un premier groupe avec une condition à remplir : sans lui, l'utilisateur aurait
     * choisi « uniquement si » sans rien à quoi répondre. Les groupes déjà composés sont conservés.
     * En sortir les efface du brouillon : « toujours affiché », c'est n'avoir aucune condition.
     *
     * Les conditions d'un élément ne sont pas perdues pour autant : elles sont mises de côté le temps
     * de la session d'édition, et reviennent si l'utilisateur repasse l'élément sous conditions.
     */
    public readonly updateDisplayMode = (target: ConditionTarget, mode: DisplayMode): void => {
        const key = this.setAsideKey(target);

        if (mode === ALWAYS_DISPLAYED) {
            const current = this.displayOptions(target);
            if (key !== null && current.groups.length > 0) this.setAsideDisplayOptions.set(key, current);

            this.updateDisplayOptions(
                target,
                (options) => new DisplayOptions({operator: options.operator, groups: []})
            );
            return;
        }

        const setAside = key === null ? undefined : this.setAsideDisplayOptions.get(key);
        if (key !== null) this.setAsideDisplayOptions.delete(key);

        this.updateDisplayOptions(target, (options) => {
            if (options.groups.length > 0) return options;

            return setAside ?? options.withGroupAdded(this.defaultConditionType(target));
        });
    };

    /** Conditions d'affichage d'une cible : un élément de la page en cours d'édition, ou une page. */
    public readonly displayOptions = (target: ConditionTarget): DisplayOptions =>
        (target.kind === 'element' ? this.selectedElements()[target.index] : this.pages()[target.index])
            ?.displayOptions ?? new DisplayOptions();

    /** Relie les groupes entre eux : tous doivent être vrais, ou l'un d'eux suffit. */
    public readonly updateDisplayOperator = (target: ConditionTarget, operator: OPERATOR_LOGICAL): void =>
        this.updateDisplayOptions(target, (options) => options.withOperator(operator));

    /** Relie entre elles les conditions d'un même groupe. */
    public readonly updateGroupOperator = (
        target: ConditionTarget,
        groupIndex: number,
        operator: OPERATOR_LOGICAL
    ): void => this.updateDisplayOptions(target, (options) => options.withGroupOperator(groupIndex, operator));

    public readonly addConditionGroup = (target: ConditionTarget): void =>
        this.updateDisplayOptions(target, (options) => options.withGroupAdded(this.defaultConditionType(target)));

    public readonly removeConditionGroup = (target: ConditionTarget, groupIndex: number): void =>
        this.updateDisplayOptions(target, (options) => options.withGroupRemoved(groupIndex));

    public readonly addCondition = (target: ConditionTarget, groupIndex: number): void =>
        this.updateDisplayOptions(target, (options) =>
            options.withConditionAdded(groupIndex, this.defaultConditionType(target))
        );

    /**
     * Sur quoi porte une condition qui s'ouvre : la réponse, sauf là où aucune n'a encore été
     * donnée — devant la première question de l'enquête, il ne reste que les métadonnées.
     */
    private readonly defaultConditionType = (target: ConditionTarget): CONDITION_TYPE =>
        this.canConditionOnAnswer(target) ? CONDITION_TYPE.ANSWER : CONDITION_TYPE.METADATA;

    /** La dernière condition d'un groupe emporte le groupe ; le dernier groupe, l'affichage sous condition. */
    public readonly removeCondition = (target: ConditionTarget, groupIndex: number, conditionIndex: number): void =>
        this.updateDisplayOptions(target, (options) => options.withConditionRemoved(groupIndex, conditionIndex));

    public readonly updateCondition = (
        target: ConditionTarget,
        groupIndex: number,
        conditionIndex: number,
        patch: Partial<Condition>
    ): void => this.updateDisplayOptions(target, (options) => options.withCondition(groupIndex, conditionIndex, patch));

    /**
     * Pages qu'une condition peut viser, avec les questions qu'elles offrent.
     *
     * Un élément voit ce qui le précède : les questions des pages antérieures, et celles posées avant
     * lui sur sa propre page. La page de fin vient après tout le questionnaire, ses éléments voient
     * donc tout. Une page, elle, ne voit que les pages qui la précèdent — jamais les questions
     * qu'elle porte, puisqu'elle se montre ou non avant qu'on y réponde.
     */
    public readonly conditionSources = (target: ConditionTarget): ConditionPageOption[] => {
        const draft = this.draftSignal();
        if (!draft) return [];

        if (target.kind === 'page') return draft.conditionSourcesBefore(target.index, 0);

        const selection = this.selectionSignal();
        const pageIndex = selection.kind === 'conclusion' ? draft.pages.length : selection.index;

        return draft.conditionSourcesBefore(pageIndex, target.index);
    };

    /**
     * Une condition ne peut porter sur une réponse que s'il y en a déjà une. Devant la première
     * question de l'enquête, il ne reste donc que les métadonnées.
     */
    public readonly canConditionOnAnswer = (target: ConditionTarget): boolean =>
        this.conditionSources(target).length > 0;

    /**
     * Étapes du parcours, telles que l'enquête les définit. Vide pour une enquête qui n'en a pas —
     * c'est-à-dire pour tout ce qui n'est pas une enquête par étapes.
     */
    public readonly stages = computed<Stage[]>(() => this.surveyHeader()?.stages ?? []);

    /**
     * Le catalogue peut-il encore poser une question de ce type ?
     *
     * Le brouillon en porte la règle, mais elle ne se tranche pas sur lui seul : le niveau d'effort
     * d'une enquête par étapes se compte en étapes libres, et les étapes sont à l'en-tête.
     */
    public readonly acceptsQuestionType = (questionType: QUESTION_TYPE): boolean =>
        this.draft()?.acceptsQuestionType(questionType, this.stages()) ?? true;

    /**
     * Une question doit-elle dire de quelle étape du parcours elle parle ?
     *
     * Seule une enquête par étapes le demande, et seulement de celles de ses questions qui se
     * mesurent étape par étape : ailleurs le rattachement n'aurait rien à désigner.
     *
     * Le réglage paraît même quand l'enquête ne définit aucune étape, la liste restant vide : c'est
     * ce qui donne à voir ce que la diffusion réclame, là où un champ absent laisserait l'exigence
     * inexplicable.
     */
    public readonly elementNeedsStage = (elementIndex: number): boolean =>
        this.surveyHeader()?.type === SurveyType.SATISFACTION_STAGES &&
        (this.selectedElements()[elementIndex]?.isStaged() ?? false);

    /**
     * Étapes qu'une question peut désigner : celles qu'un autre niveau d'effort occupe déjà en sont
     * retirées, l'effort ne se mesurant qu'une fois par étape. Le modèle en porte la règle.
     */
    public readonly elementStages = (elementIndex: number): Stage[] => {
        const element = this.selectedElements()[elementIndex];
        const draft = this.draftSignal();
        if (!element || !draft) return this.stages();

        return draft.stagesAvailableFor(this.stages(), element);
    };

    /** Étape à laquelle une question est rattachée, ou `null` tant qu'elle ne l'est pas. */
    public readonly elementStageId = (elementIndex: number): number | null =>
        this.selectedElements()[elementIndex]?.stageId ?? null;

    /**
     * Rattache une question à une étape du parcours. C'est sous elle que ses réponses seront
     * restituées — sans quoi elles ne se rattacheraient à rien, et l'enquête ne serait pas diffusable.
     */
    public readonly updateElementStage = (elementIndex: number, stageId: number | null): void =>
        this.updateElement(elementIndex, () => ({stageId}));

    /**
     * La page peut-elle voir toutes ses questions affectées d'un coup à une étape ?
     *
     * Seule une enquête par étapes le propose, et seulement sur une page qui pose au moins une
     * question se mesurant étape par étape : ailleurs il n'y aurait rien à rattacher.
     */
    public readonly canAssignPageStage = (pageIndex: number): boolean =>
        this.surveyHeader()?.type === SurveyType.SATISFACTION_STAGES &&
        (this.pages()[pageIndex]?.hasStagedQuestion() ?? false);

    /** La page pose-t-elle un niveau d'effort ? Ses étapes déjà mesurées ne sont alors plus offertes. */
    public readonly pageHasCes = (pageIndex: number): boolean => this.pages()[pageIndex]?.hasCes() ?? false;

    /** Étapes auxquelles toutes les questions d'une page peuvent être affectées ; le modèle en porte la règle. */
    public readonly pageStages = (pageIndex: number): Stage[] => {
        const page = this.pages()[pageIndex];
        const draft = this.draftSignal();
        if (!page || !draft) return this.stages();

        return draft.stagesAvailableForPage(this.stages(), page);
    };

    /**
     * L'une des questions à rattacher de la page figure-t-elle dans la version en ligne ?
     *
     * Même règle que pour le changement d'étape d'une seule question (`isQuestionInPublishedVersion`) :
     * ses réponses changeraient d'onglet dans les tableaux de bord, l'affectation s'annonce d'abord.
     */
    public readonly pageHasPublishedStagedQuestion = (pageIndex: number): boolean =>
        (this.pages()[pageIndex]?.elements ?? []).some(
            (element) =>
                element.isStaged() &&
                element.id !== undefined &&
                element.id !== null &&
                this.publishedElementIds().has(element.id)
        );

    /** Rattache à une même étape toutes les questions de la page qui se mesurent étape par étape. */
    public readonly assignPageStage = (pageIndex: number, stageId: number): void => {
        if (!this.pages()[pageIndex]) return;

        this.updatePages(
            this.pages().map((page, index) =>
                index === pageIndex
                    ? new FormPage({
                          ...page,
                          elements: page.elements.map((element) => {
                              if (!element.isStaged()) return element;

                              // Même précaution que `updateElement` : la carte doit se reconnaître.
                              const updated = new ElementPage({...element, stageId});
                              this.inheritIdentity(element, updated);

                              return updated;
                          }),
                      })
                    : page
            )
        );
    };

    /** Réponse exigée à une question de la page en cours d'édition. */
    public readonly elementMandatory = (elementIndex: number): boolean =>
        this.selectedElements()[elementIndex]?.configuration?.constraints?.mandatory?.required ?? false;

    /**
     * Exige, ou n'exige plus, une réponse à une question. Le message opposé au répondant ne bouge
     * pas : il a été rédigé pour cette question, la rendre facultative ne l'efface pas.
     */
    public readonly updateElementMandatory = (elementIndex: number, required: boolean): void =>
        this.updateElement(elementIndex, (element) => ({
            configuration: {
                ...element.configuration,
                constraints: {
                    ...element.configuration?.constraints,
                    mandatory: {...element.configuration?.constraints?.mandatory, required},
                },
            },
        }));

    /**
     * Message opposé au répondant qui laisserait sans réponse une question obligatoire, dans la
     * langue de composition.
     *
     * Il se lit que la question soit obligatoire ou non : c'est la carte qui décide de le montrer,
     * et un message rédigé puis rendu facultatif doit se retrouver tel quel si on l'exige à nouveau.
     */
    public readonly elementMandatoryTextError = (elementIndex: number): string =>
        this.selectedElements()[elementIndex]?.configuration?.constraints?.mandatory?.textError?.[LABEL_KEY] ?? '';

    /**
     * Réécrit ce message, sans toucher aux autres langues ni au reste des contraintes. Vide, il
     * n'est pas remplacé par un texte d'office : le répondant verra celui que le questionnaire
     * oppose par défaut.
     */
    public readonly updateElementMandatoryTextError = (elementIndex: number, textError: string): void =>
        this.updateElement(elementIndex, (element) => ({
            configuration: {
                ...element.configuration,
                constraints: {
                    ...element.configuration?.constraints,
                    mandatory: {
                        ...element.configuration?.constraints?.mandatory,
                        textError: {
                            ...element.configuration?.constraints?.mandatory?.textError,
                            [LABEL_KEY]: textError,
                        },
                    },
                },
            },
        }));

    /**
     * Comment les crans d'une question notée se colorent : une couleur par classe de répondant, ou
     * une seule pour toute la question.
     *
     * Comme les bornes, le mode se lit ici et non sur l'élément que la carte reçoit en entrée :
     * celui-ci ne vaut que pour la détection de changement en cours, alors que le store est à jour
     * dès l'écriture.
     */
    public readonly elementColorMode = (elementIndex: number): RatedColorMode =>
        this.selectedElements()[elementIndex]?.colorMode() ?? GRADUATED_COLORS;

    /**
     * Gradue les crans d'une question notée, ou les rend à la couleur des réponses. Les couleurs de
     * l'élément sont renseignées au passage : le questionnaire les applique telles quelles, il ne
     * les devine pas.
     */
    public readonly updateElementColorMode = (elementIndex: number, mode: RatedColorMode): void =>
        this.updateElement(elementIndex, (element) => ({configuration: element.withColorMode(mode)}));

    /** Forme sous laquelle une question notée se présente : nombres, icônes ou libellés. */
    public readonly elementRepresentation = (elementIndex: number): REPRESENTATION =>
        this.selectedElements()[elementIndex]?.representation() ?? REPRESENTATION.NUMBER;

    /** Formes que le paramétrage propose pour cette question : toutes ne valent pas de tout type. */
    public readonly elementRepresentations = (elementIndex: number): REPRESENTATION[] =>
        this.selectedElements()[elementIndex]?.representations() ?? RATED_REPRESENTATIONS;

    public readonly updateElementRepresentation = (elementIndex: number, representation: REPRESENTATION): void =>
        this.updateElement(elementIndex, (element) => ({configuration: element.withRepresentation(representation)}));

    /** Dessin d'une question notée en icônes : l'étoile, ou le smiley. */
    public readonly elementVisualEmoji = (elementIndex: number): VISUAL_EMOJI =>
        this.selectedElements()[elementIndex]?.visualEmoji() ?? VISUAL_EMOJI.STAR;

    public readonly updateElementVisualEmoji = (elementIndex: number, emoji: VISUAL_EMOJI): void =>
        this.updateElement(elementIndex, (element) => ({configuration: element.withVisualEmoji(emoji)}));

    /** Étendue sur laquelle une satisfaction se note : de un à quatre, de un à cinq, de zéro à dix. */
    public readonly elementCsatRange = (elementIndex: number): CSAT_RANGE =>
        this.selectedElements()[elementIndex]?.csatRange() ?? CSAT_RANGE.ZERO_TO_TEN;

    /**
     * Rallonge ou raccourcit l'échelle d'une satisfaction. Ses couleurs suivent : elles vont une par
     * cran, celles de l'ancienne étendue n'auraient plus le compte.
     */
    public readonly updateElementCsatRange = (elementIndex: number, range: CSAT_RANGE): void =>
        this.updateElement(elementIndex, (element) => ({configuration: element.withCsatRange(range)}));

    /** Étendue sur laquelle un effort se note en nombres : de un à cinq, de un à sept, de zéro à dix. */
    public readonly elementCesRange = (elementIndex: number): CES_RANGE =>
        this.selectedElements()[elementIndex]?.cesRange() ?? CES_RANGE.ZERO_TO_TEN;

    /** Rallonge ou raccourcit l'échelle d'un effort ; ses couleurs suivent, une par cran. */
    public readonly updateElementCesRange = (elementIndex: number, range: CES_RANGE): void =>
        this.updateElement(elementIndex, (element) => ({configuration: element.withCesRange(range)}));

    /** Échelle de libellés d'un effort noté en toutes lettres : facilité, force ou simplicité. */
    public readonly elementCesTextRepresentation = (elementIndex: number): CES_TEXT_REPRESENTATION =>
        this.selectedElements()[elementIndex]?.cesTextRepresentation() ?? CES_TEXT_REPRESENTATION.EASY;

    /**
     * Change les mots par lesquels un effort se dit. Les libellés eux-mêmes ne bougent pas :
     * l'élément porte ceux de chaque échelle, et n'affiche que ceux de l'échelle retenue.
     */
    public readonly updateElementCesTextRepresentation = (
        elementIndex: number,
        typeRepresentation: CES_TEXT_REPRESENTATION
    ): void =>
        this.updateElement(elementIndex, (element) => ({
            configuration: element.withCesTextRepresentation(typeRepresentation),
        }));

    /** Premier cran d'une échelle d'évaluation notée en nombres : zéro ou un. */
    public readonly elementScaleStart = (elementIndex: number): number =>
        this.selectedElements()[elementIndex]?.scaleStart() ?? DEFAULT_SCALE_START;

    public readonly updateElementScaleStart = (elementIndex: number, start: number): void =>
        this.updateElement(elementIndex, (element) => ({configuration: element.withScaleStart(start)}));

    /**
     * Longueur d'une échelle d'évaluation : son dernier cran en nombres, son nombre d'icônes en
     * dessins. Les deux formes partagent la même, seule la façon de la présenter change.
     */
    public readonly elementScaleRange = (elementIndex: number): number =>
        this.selectedElements()[elementIndex]?.scaleRange() ?? DEFAULT_SCALE_RANGE;

    public readonly updateElementScaleRange = (elementIndex: number, range: number): void =>
        this.updateElement(elementIndex, (element) => ({configuration: element.withScaleRange(range)}));

    /** Retitre un élément de la page en cours d'édition, sans toucher au reste de ce qu'il porte. */
    public readonly updateElementLabels = (elementIndex: number, labels: PageLabels): void =>
        this.updateElement(elementIndex, (element) => withLabels(element, labels));

    /** Libellés des critères d'un élément, dans l'ordre où le répondant les notera. */
    public readonly elementSubQuestionLabels = (elementIndex: number): string[] =>
        this.selectedElements()[elementIndex]?.criterionLabels() ?? [];

    /**
     * Renomme les critères d'un élément, un libellé par critère et dans le même ordre. Les critères
     * eux-mêmes ne bougent pas : leur identifiant et leurs autres traductions sont préservés.
     */
    public readonly updateElementSubQuestionLabels = (elementIndex: number, labels: string[]): void =>
        this.updateElement(elementIndex, (element) => ({
            subQuestions: element.withCriterionLabels(labels),
        }));

    /**
     * Ajoute un critère vide à la suite des autres, prêt à être nommé.
     *
     * Il part au serveur tel quel, sans libellé : c'est l'enregistrement qui lui attribue son
     * identifiant, celui sous lequel les réponses à ce critère seront rangées.
     */
    public readonly addElementSubQuestion = (elementIndex: number): void =>
        this.updateElement(elementIndex, (element) => ({subQuestions: element.withCriterionAdded()}));

    /**
     * Déplace un critère dans la liste de ceux qu'une question fait noter.
     *
     * L'ordre est celui dans lequel le répondant les notera. Les critères gardent leur identifiant en
     * changeant de rang : les réponses déjà rangées sous l'un d'eux et les conditions d'affichage qui
     * l'observaient continuent de le viser.
     */
    public readonly moveElementSubQuestion = (elementIndex: number, from: number, to: number): void =>
        this.updateElement(elementIndex, (element) => ({subQuestions: element.withCriterionMoved(from, to)}));

    /**
     * Un critère décide-t-il quelque part de ce qui s'affiche ? C'est ce qu'on annonce avant de le
     * supprimer, les conditions qui l'observent devant partir avec lui.
     *
     * Un critère que le serveur n'a pas encore enregistré n'a pas d'identifiant : aucune condition ne
     * pouvait le désigner, il n'en emporte donc aucune.
     */
    public readonly isSubQuestionUsedInConditions = (elementIndex: number, subQuestionRank: number): boolean => {
        const element = this.selectedElements()[elementIndex];
        const subQuestionId = element?.subQuestions?.[subQuestionRank]?.id;
        if (!element?.id || subQuestionId === undefined || subQuestionId === null) return false;

        return this.draftSignal()?.usesSubQuestionInConditions(element.id, subQuestionId) ?? false;
    };

    /**
     * Retire un critère, et avec lui les conditions d'affichage qui l'observaient — c'est ce que
     * l'avertissement promet à l'utilisateur avant qu'il confirme.
     *
     * Le critère se désigne par son rang : celui qui vient d'être ajouté n'a pas encore
     * d'identifiant, et se retire comme les autres.
     */
    public readonly removeElementSubQuestion = (elementIndex: number, subQuestionRank: number): void => {
        const element = this.selectedElements()[elementIndex];
        if (!element?.subQuestions?.[subQuestionRank]) return;

        const {id: subQuestionId} = element.subQuestions[subQuestionRank];
        this.updateElement(elementIndex, (current) => ({
            subQuestions: current.withCriterionRemoved(subQuestionRank),
        }));

        // Les conditions ensuite, sur le brouillon que le retrait vient de remplacer. Rien à
        // nettoyer si le serveur n'avait encore nommé ni la question ni le critère : aucune condition
        // n'aurait pu les désigner.
        const draft = this.draftSignal();
        if (!draft || !element.id || subQuestionId === undefined || subQuestionId === null) return;

        this.replaceDraft(draft.withoutSubQuestionConditions(element.id, subQuestionId));
    };

    /**
     * Réglages d'une invitation à déposer un avis : la plateforme visée, le lien, les couleurs du
     * bouton et la façon de l'ouvrir. Ils se lisent ensemble, comme ils se règlent.
     */
    public readonly elementReview = (elementIndex: number): PlatformReviewForm =>
        this.selectedElements()[elementIndex]?.reviewForm() ?? DEFAULT_PLATFORM_REVIEW_FORM;

    /**
     * Vise une autre plateforme. Le bouton se refait à ses couleurs : il porte son nom, il doit se
     * reconnaître à sa livrée.
     */
    public readonly updateElementReviewSite = (elementIndex: number, site: TargetPlatformReview): void =>
        this.updateElement(elementIndex, (element) => element.withReviewSite(site));

    /** Adresse de la page où le répondant déposera son avis. */
    public readonly updateElementReviewLink = (elementIndex: number, link: string): void =>
        this.updateElement(elementIndex, () => ({link}));

    public readonly updateElementReviewButtonFontColor = (elementIndex: number, buttonFontColor: string): void =>
        this.updateElement(elementIndex, () => ({buttonFontColor}));

    public readonly updateElementReviewButtonBackgroundColor = (
        elementIndex: number,
        buttonBackgroundColor: string
    ): void => this.updateElement(elementIndex, () => ({buttonBackgroundColor}));

    /** Le lien s'ouvre à côté du questionnaire plutôt qu'à sa place. */
    public readonly updateElementReviewOpenNewTab = (elementIndex: number, openNewTab: boolean): void =>
        this.updateElement(elementIndex, () => ({openNewTab}));

    /** Contenu enrichi d'une zone de texte, dans la langue de composition. */
    public readonly elementMessage = (elementIndex: number): string =>
        this.selectedElements()[elementIndex]?.message?.[LABEL_KEY] ?? '';

    /** Réécrit le contenu d'une zone de texte, sans toucher aux autres langues. */
    public readonly updateElementMessage = (elementIndex: number, message: string): void =>
        this.updateElement(elementIndex, (element) => ({message: {...element.message, [LABEL_KEY]: message}}));

    /** Libellés des choix d'un élément, dans l'ordre où ils sont proposés. */
    public readonly elementChoiceLabels = (elementIndex: number): string[] =>
        this.selectedElements()[elementIndex]?.choiceLabels() ?? [];

    /** Renomme les choix d'un élément ; leurs identifiants et autres traductions sont préservés. */
    public readonly updateElementChoiceLabels = (elementIndex: number, labels: string[]): void =>
        this.updateElement(elementIndex, (element) => ({choices: element.withChoiceLabels(labels)}));

    /**
     * Ajoute un choix vide, prêt à être nommé.
     *
     * Il part au serveur tel quel, sans libellé : c'est l'enregistrement qui lui attribue son
     * identifiant, et une condition d'affichage désigne le choix qu'elle vise par cet identifiant.
     * Attendre une frappe pour l'enregistrer le rendrait invisible aux conditions jusque-là.
     */
    public readonly addElementChoice = (elementIndex: number): void =>
        this.updateElement(elementIndex, (element) => ({choices: element.withChoiceAdded()}));

    /**
     * Déplace un choix dans la liste de ceux qu'une question propose.
     *
     * L'ordre est celui dans lequel le répondant les lira. Les choix gardent leur identifiant en
     * changeant de rang : une condition d'affichage qui en observait un continue de le viser.
     */
    public readonly moveElementChoice = (elementIndex: number, from: number, to: number): void =>
        this.updateElement(elementIndex, (element) => ({choices: element.withChoiceMoved(from, to)}));

    /**
     * Un choix décide-t-il quelque part de ce qui s'affiche ? C'est ce qu'on annonce avant de le
     * supprimer, les conditions qui l'observent devant partir avec lui.
     *
     * Un choix que le serveur n'a pas encore enregistré n'a pas d'identifiant : aucune condition ne
     * pouvait le désigner, il n'en emporte donc aucune.
     */
    public readonly isChoiceUsedInConditions = (elementIndex: number, choiceRank: number): boolean => {
        const element = this.selectedElements()[elementIndex];
        const choiceId = element?.choices?.[choiceRank]?.id;
        if (!element?.id || choiceId === undefined || choiceId === null) return false;

        return this.draftSignal()?.usesChoiceInConditions(element.id, choiceId) ?? false;
    };

    /**
     * Retire un choix, et avec lui les conditions d'affichage qui l'observaient — c'est ce que
     * l'avertissement promet à l'utilisateur avant qu'il confirme.
     *
     * Le choix se désigne par son rang : celui qui vient d'être ajouté n'a pas encore
     * d'identifiant, et se retire comme les autres.
     */
    public readonly removeElementChoice = (elementIndex: number, choiceRank: number): void => {
        const element = this.selectedElements()[elementIndex];
        if (!element?.choices?.[choiceRank]) return;

        const {id: choiceId} = element.choices[choiceRank];
        this.updateElement(elementIndex, (current) => ({choices: current.withChoiceRemoved(choiceRank)}));

        // Les conditions ensuite, sur le brouillon que le retrait vient de remplacer. Rien à
        // nettoyer si le serveur n'avait encore nommé ni la question ni le choix : aucune condition
        // n'aurait pu les désigner.
        const draft = this.draftSignal();
        if (!draft || !element.id || choiceId === undefined || choiceId === null) return;

        this.replaceDraft(draft.withoutChoiceConditions(element.id, choiceId));
    };

    /** Combien de choix le répondant peut désigner : un seul, ou autant qu'il en reconnaît. */
    public readonly elementChoicesAnswerType = (elementIndex: number): CHOICES_ANSWER_TYPE =>
        this.selectedElements()[elementIndex]?.choicesAnswerType() ?? CHOICES_ANSWER_TYPE.UNIQUE;

    public readonly updateElementChoicesAnswerType = (elementIndex: number, type: CHOICES_ANSWER_TYPE): void =>
        this.updateElement(elementIndex, (element) => ({configuration: element.withChoicesAnswerType(type)}));

    /**
     * Les répondants ont-ils déjà vu cette question ?
     *
     * Deux conditions, et les deux comptent : l'enquête a été diffusée — sans invitation partie,
     * personne n'a rien pu répondre —, et la question figurait dans la version en ligne. Une question
     * posée depuis n'a aucune réponse à préserver, et c'est bien ce qui la distingue : d'où sa
     * recherche dans la version publiée, seule à dire ce qui était en ligne.
     *
     * C'est ce qui décide de tout ce qui protège les réponses déjà reçues : le type de réponse se
     * fige, et le reste — libellés, choix, critères — s'avertit avant d'être touché.
     */
    public readonly isPublishedQuestion = (elementIndex: number): boolean =>
        !!this.surveyHeader()?.scheduled && this.isQuestionInPublishedVersion(elementIndex);

    /**
     * La question figure-t-elle dans la version en ligne ?
     *
     * Sans regarder si une diffusion est partie : une enquête publiée se répond aussi par URL ou
     * anonymement, sans invitation, et ces réponses-là comptent autant. C'est ce qui décide de ce
     * qui touche à la restitution — changer l'étape d'une question déplace ses réponses dans les
     * tableaux de bord, qu'elles soient venues d'une invitation ou d'un lien.
     *
     * Le brouillon a beau avoir été retouché depuis, la version publiée reste celle que les
     * répondants voient : c'est elle seule qui compte.
     */
    public readonly isQuestionInPublishedVersion = (elementIndex: number): boolean => {
        const id = this.selectedElements()[elementIndex]?.id;

        return id !== undefined && id !== null && this.publishedElementIds().has(id);
    };

    /**
     * Le type de réponse d'une question se règle-t-il encore ?
     *
     * Il se fige dès que les répondants ont vu la question : passer d'un choix unique à un choix
     * multiple, ou l'inverse, changerait la nature des réponses déjà reçues, et rendrait incomparable
     * ce qui a été recueilli avant et après. Interdit, et non simplement averti — c'est le seul
     * réglage qu'aucune correction ne rattraperait.
     */
    public readonly canChangeChoicesAnswerType = (elementIndex: number): boolean =>
        !this.isPublishedQuestion(elementIndex);

    /** Sens dans lequel les choix se rangent : un par ligne, ou plusieurs. */
    public readonly elementChoicesLayout = (elementIndex: number): CHOICES_LAYOUT =>
        this.selectedElements()[elementIndex]?.choicesLayout() ?? CHOICES_LAYOUT.VERTICAL;

    public readonly updateElementChoicesLayout = (elementIndex: number, layout: CHOICES_LAYOUT): void =>
        this.updateElement(elementIndex, (element) => ({configuration: element.withChoicesLayout(layout)}));

    /** Forme donnée aux choix : des boutons tous visibles, ou une liste déroulante. */
    public readonly elementChoicesDisplay = (elementIndex: number): CHOICES_DISPLAY =>
        this.selectedElements()[elementIndex]?.choicesDisplay() ?? CHOICES_DISPLAY.BUTTON;

    public readonly updateElementChoicesDisplay = (elementIndex: number, display: CHOICES_DISPLAY): void =>
        this.updateElement(elementIndex, (element) => ({configuration: element.withChoicesDisplay(display)}));

    /**
     * Identifiants des éléments déjà en ligne. C'est ce qui distingue une question que les
     * répondants ont vue d'une question posée depuis : la seconde ne figure pas dans la version
     * publiée, quelle que soit la session où elle a été ajoutée.
     */
    private readonly publishedElementIds = computed<Set<number>>(() => {
        const published = this.surveyFull()?.publishedVersion;
        if (!published) return new Set();

        const elements = [
            ...published.pages.flatMap((page) => page.elements),
            ...(published.conclusionPage?.elements ?? []),
        ];

        return new Set(
            elements.map((element) => element.id).filter((id): id is number => id !== undefined && id !== null)
        );
    });

    /** Renomme les deux extrémités de l'échelle d'un élément, sans toucher au reste de son paramétrage. */
    public readonly updateElementBoundTexts = (elementIndex: number, boundTexts: BoundTexts): void =>
        this.updateElement(elementIndex, (element) => ({
            configuration: {
                ...element.configuration,
                boundTexts: {...element.configuration?.boundTexts, [LABEL_KEY]: boundTexts},
            },
        }));

    /**
     * Dépose un élément dans la page en cours d'édition, au rang voulu — page de fin comprise.
     *
     * L'élément est une copie du modèle de son type : le catalogue ne livre qu'un type, c'est le
     * serveur qui dit ce qu'est un NPS ou un CES. Sans modèle chargé, rien n'est déposé : mieux
     * vaut un dépôt sans effet qu'une question vidée de son paramétrage.
     *
     * Un rang hors des bornes est ramené dedans : le dépôt en pleine page ne doit pas dépendre du
     * pixel visé.
     */
    public readonly addElement = (type: ELEMENT_TYPE, index: number): void => {
        const draft = this.draftSignal();
        const template = this.templateOf(type);
        if (!draft || !template) return;

        // Un indicateur ne se mesure qu'une fois par enquête, et le niveau d'effort une fois par
        // étape. Le catalogue éteint déjà la carte, mais c'est le brouillon qui en répond : lui seul
        // sait ce que les autres pages mesurent.
        if (template.isQuestion() && !this.acceptsQuestionType(type as QUESTION_TYPE)) return;

        const elements = this.selectedElements();
        const rank = Math.min(Math.max(index, 0), elements.length);
        const added = [...elements];
        added.splice(rank, 0, template.copyForPage().withTranslatedLabels(this.translateLabel));

        this.replaceSelectedElements(added);
    };

    /**
     * Déplace un élément dans la page en cours d'édition. Leur ordre est celui du parcours : le
     * répondant les rencontre tels qu'ils se suivent ici.
     */
    public readonly moveElement = (from: number, to: number): void => {
        const moved = this.draftWithElementMoved(from, to);
        if (moved) this.replaceDraft(moved);
    };

    /**
     * Le déplacement d'un élément laisserait-il une condition d'affichage sans la réponse qu'elle
     * observe ? C'est ce qu'on demande avant de déplacer, pour l'annoncer.
     *
     * Les deux sens comptent : l'élément déplacé peut passer devant ce qu'il observe, et il peut tout
     * aussi bien passer derrière un élément qui observait sa réponse.
     */
    public readonly elementMoveBreaksConditions = (from: number, to: number): boolean =>
        this.breaksConditions(this.draftWithElementMoved(from, to));

    /**
     * Déplace un élément en abandonnant les conditions d'affichage que le déplacement rend
     * impossibles — c'est ce que l'avertissement promet à l'utilisateur avant qu'il confirme.
     */
    public readonly moveElementDroppingBrokenConditions = (from: number, to: number): void => {
        const moved = this.draftWithElementMoved(from, to);
        if (!moved) return;

        this.replaceDraft(this.withoutBrokenConditions(moved));
    };

    /**
     * Le brouillon tel qu'un déplacement d'élément dans la page en cours le laisserait ; `null` si le
     * déplacement n'a pas lieu.
     */
    private readonly draftWithElementMoved = (from: number, to: number): Survey | null => {
        const elements = this.selectedElements();
        if (from === to || !elements[from] || !elements[to]) return null;

        const moved = [...elements];
        const [element] = moved.splice(from, 1);
        moved.splice(to, 0, element);

        return this.draftWithSelectedElements(moved);
    };

    /**
     * Le déplacement rompt-il une condition d'affichage qui tenait encore ?
     *
     * C'est la comparaison de l'avant et de l'après qui le dit, et non le seul état d'après : une
     * condition déjà orpheline — la question qu'elle observait a été supprimée depuis — ferait sans
     * cela avertir à chaque déplacement du questionnaire, pour une rupture qui ne vient pas de là.
     */
    private readonly breaksConditions = (moved: Survey | null): boolean => {
        const draft = this.draftSignal();
        if (!moved || !draft) return false;

        return moved.unreachableAnswerConditionCount() > draft.unreachableAnswerConditionCount();
    };

    /**
     * Un brouillon débarrassé des conditions que le déplacement rend impossibles, l'éditeur gardant
     * ses cartes : le nettoyage reconstruit chaque élément, leur identité doit donc les suivre.
     */
    private readonly withoutBrokenConditions = (moved: Survey): Survey => {
        const cleaned = moved.withoutUnreachableAnswerConditions();
        this.inheritIdentities(moved, cleaned);

        return cleaned;
    };

    /**
     * Un élément peut-il être retiré ?
     *
     * Tout élément l'est, sauf la dernière question du questionnaire : une enquête qui ne demande
     * plus rien n'a pas d'objet. Une zone de texte ou une invitation à déposer un avis se retirent
     * toujours — les enlever ne prive l'enquête d'aucune réponse.
     */
    public readonly canRemoveElement = (elementIndex: number): boolean => {
        const element = this.selectedElements()[elementIndex];
        if (!element) return false;

        return !element.isQuestion() || (this.draftSignal()?.questionCount() ?? 0) > 1;
    };

    /**
     * Retire un élément de la page en cours d'édition, et avec lui les conditions d'affichage qui
     * s'adossaient à sa réponse — c'est ce que l'avertissement promet à l'utilisateur avant qu'il
     * confirme.
     *
     * Le paramétrage déplié suit ce qu'il devient : celui de l'élément retiré n'a plus d'objet et se
     * referme, et celui d'un élément qui le suivait recule d'un rang avec lui — sans quoi le panneau
     * ouvert désignerait soudain la question voisine.
     */
    public readonly removeElement = (elementIndex: number): void => {
        const remaining = this.draftWithoutElement(elementIndex);
        if (!remaining) return;

        this.replaceDraft(this.withoutBrokenConditions(remaining));

        const selected = this.selectedElementIndexSignal();
        if (selected === null || selected < elementIndex) return;

        this.selectedElementIndexSignal.set(selected === elementIndex ? null : selected - 1);
    };

    /**
     * La suppression d'un élément emporterait-elle des conditions d'affichage ?
     *
     * Ailleurs dans le questionnaire, ce qui s'adossait à sa réponse n'aurait plus rien à observer.
     * C'est ce qu'on annonce avant de supprimer.
     */
    public readonly elementDeletionBreaksConditions = (elementIndex: number): boolean =>
        this.breaksConditions(this.draftWithoutElement(elementIndex));

    /** Le brouillon tel que le retrait d'un élément le laisserait ; `null` si le retrait n'a pas lieu. */
    private readonly draftWithoutElement = (elementIndex: number): Survey | null => {
        const elements = this.selectedElements();
        if (!elements[elementIndex] || !this.canRemoveElement(elementIndex)) return null;

        return this.draftWithSelectedElements(elements.filter((_, index) => index !== elementIndex));
    };

    /** Applique une modification d'habillage au brouillon : logo, police ou couleurs. */
    public readonly updateThemeConfiguration = (patch: Partial<ThemeConfiguration>): void =>
        this.updateInformation((information) => ({
            ...information,
            theme: {
                ...information.theme,
                themeConfiguration: {...information.theme.themeConfiguration, ...patch},
            },
        }));

    /** Applique une modification de comportement au brouillon, dont l'animation entre les pages. */
    public readonly updateFormConfiguration = (patch: Partial<FormConfiguration>): void =>
        this.updateInformation((information) => ({
            ...information,
            configuration: {...information.configuration, ...patch},
        }));

    /** Remplace un élément de la page en cours d'édition par lui-même, retouché. */
    private readonly updateElement = (
        elementIndex: number,
        patch: (element: ElementPage) => Partial<ElementPage>
    ): void => {
        const elements = this.selectedElements();
        const element = elements[elementIndex];
        if (!element) return;

        this.replaceSelectedElements(
            elements.map((current, index) => {
                if (index !== elementIndex) return current;

                // Retoucher un élément le remplace par un objet neuf : son identité le suit, sinon
                // l'éditeur ne reconnaîtrait plus sa carte et la referait à chaque frappe.
                const updated = new ElementPage({...current, ...patch(current)});
                this.inheritIdentity(current, updated);

                return updated;
            })
        );
    };

    /** Repose les éléments sur la page en cours d'édition, page de fin comprise. */
    private readonly replaceSelectedElements = (elements: ElementPage[]): void => {
        const draft = this.draftWithSelectedElements(elements);
        if (draft) this.replaceDraft(draft);
    };

    /**
     * Le brouillon, les éléments de la page en cours d'édition remplacés — page de fin comprise.
     *
     * Il est rendu plutôt que retenu : un déplacement doit pouvoir être examiné avant d'être appliqué,
     * pour savoir ce qu'il emporterait.
     */
    private readonly draftWithSelectedElements = (elements: ElementPage[]): Survey | null => {
        const draft = this.draftSignal();
        if (!draft) return null;

        const selection = this.selectionSignal();

        if (selection.kind === 'conclusion') {
            if (!draft.conclusionPage) return null;

            return new Survey({...draft, conclusionPage: {...draft.conclusionPage, elements}});
        }

        return new Survey({
            ...draft,
            pages: draft.pages.map((page, index) =>
                index === selection.index ? new FormPage({...page, elements}) : page
            ),
        });
    };

    /**
     * Toute retouche des conditions passe par ici : le modèle en porte les règles, le store les
     * applique là où la cible les tient — sur un élément de la page en cours, ou sur une page.
     */
    private readonly updateDisplayOptions = (
        target: ConditionTarget,
        update: (displayOptions: DisplayOptions) => DisplayOptions
    ): void => {
        if (target.kind === 'element') {
            this.updateElement(target.index, (element) => ({displayOptions: update(element.displayOptions)}));
            return;
        }

        const page = this.pages()[target.index];
        if (!page) return;

        this.updatePages(
            this.pages().map((current, index) =>
                index === target.index
                    ? new FormPage({...current, displayOptions: update(current.displayOptions)})
                    : current
            )
        );
    };

    private readonly updateInformation = (update: (information: SurveyInformation) => SurveyInformation): void => {
        const draft = this.draftSignal();
        if (!draft) return;

        this.replaceDraft(new Survey({...draft, information: update(draft.information)}));
    };

    private readonly updatePages = (pages: FormPage[]): void => {
        const draft = this.draftSignal();
        if (!draft) return;

        this.replaceDraft(new Survey({...draft, pages}));
    };

    private readonly replaceDraft = (draft: Survey): void => {
        this.draftSignal.set(draft);
        // Ce qui est à l'écran ne correspond plus à ce qui est en ligne : l'en-tête le dit sans
        // attendre l'enregistrement, qui est différé.
        this.draftEditedSignal.set(true);
        this.draftToSave.next(draft);
    };

    /**
     * Reprend le brouillon que le serveur renvoie, qui est le même à ceci près qu'il porte les
     * identifiants qu'il vient d'attribuer aux pages et aux éléments créés dans la session.
     *
     * Sans cela une question tout juste posée garderait `id: null` jusqu'au prochain chargement, et
     * aucune condition d'affichage ne pourrait la désigner — une `Condition` vise sa cible par son
     * identifiant.
     *
     * La reprise n'a lieu que si rien n'a bougé depuis l'envoi. L'égalité de référence suffit à le
     * dire : toute modification remplace le brouillon par une nouvelle instance. Sinon on laisse la
     * saisie en cours tranquille — elle a déjà programmé son propre enregistrement, dont la réponse
     * apportera les identifiants dès que l'utilisateur marquera une pause.
     *
     * Écriture directe du signal, sans repasser par `replaceDraft` : réenregistrer ce que le serveur
     * vient de renvoyer relancerait la boucle sans fin.
     */
    private readonly adoptSavedDraft = (sent: Survey, saved: Survey): void => {
        if (this.draftSignal() !== sent) return;

        this.inheritIdentities(sent, saved);
        this.draftSignal.set(saved);
    };

    /**
     * Identité de chaque élément, telle que l'éditeur suit ses cartes d'une version du brouillon à
     * la suivante.
     *
     * Elle ne peut pas vivre dans le modèle : tout ce qu'un élément porte repart au serveur, et une
     * clé de l'éditeur n'a rien à y faire. Elle ne peut pas non plus se déduire de l'élément — deux
     * questions du même type tout juste posées sont identiques, `id` compris, qui reste `null`
     * jusqu'à l'enregistrement. Elle est donc tenue à côté, attachée à l'objet lui-même.
     *
     * Le type la compose pour qu'elle se lise : `NPS#3` dit de quoi il s'agit, le rang de création
     * la rend unique.
     */
    private readonly identities = new WeakMap<ElementPage, string>();

    private identityCount = 0;

    /** Identité d'un élément, attribuée à la première rencontre et conservée ensuite. */
    public readonly elementIdentity = (element: ElementPage): string => {
        const known = this.identities.get(element);
        if (known) return known;

        const identity = `${element.type ?? 'ELEMENT'}#${this.identityCount++}`;
        this.identities.set(element, identity);

        return identity;
    };

    /**
     * Conditions d'affichage mises de côté quand un élément repasse « toujours affiché », rangées
     * sous l'identité de l'élément.
     *
     * Elles ne vivent pas dans le brouillon : l'API lit l'absence d'options comme « toujours
     * affiché », et des conditions enregistrées y seraient évaluées. Elles sont donc tenues à côté,
     * le temps de la session d'édition — le store repart vierge avec la suivante.
     */
    private readonly setAsideDisplayOptions = new Map<string, DisplayOptions>();

    /** Sous quelle clé mettre de côté les conditions d'une cible : seuls les éléments en ont une. */
    private readonly setAsideKey = (target: ConditionTarget): string | null => {
        if (target.kind !== 'element') return null;

        const element = this.selectedElements()[target.index];

        return element ? this.elementIdentity(element) : null;
    };

    private readonly inheritIdentity = (from: ElementPage, to: ElementPage): void => {
        const identity = this.identities.get(from);
        if (identity) this.identities.set(to, identity);
    };

    /**
     * Reporte les identités d'un brouillon sur celui que le serveur renvoie.
     *
     * La réponse est le brouillon envoyé, aux identifiants près : mêmes pages, mêmes éléments, dans
     * le même ordre — mais tout y a été reconstruit depuis le JSON. Sans ce report, l'éditeur ne
     * reconnaîtrait plus aucune carte et les referait toutes à chaque enregistrement, emportant le
     * curseur de qui aurait laissé le sien dans un champ.
     */
    private readonly inheritIdentities = (sent: Survey, saved: Survey): void => {
        const sentElements = [...sent.pages.flatMap((page) => page.elements), ...(sent.conclusionPage?.elements ?? [])];
        const savedElements = [
            ...saved.pages.flatMap((page) => page.elements),
            ...(saved.conclusionPage?.elements ?? []),
        ];

        if (sentElements.length !== savedElements.length) return;

        sentElements.forEach((element, rank) => this.inheritIdentity(element, savedElements[rank]));
    };
}
